import { useState } from "react";
import PageSEO from "@/components/PageSEO";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { Check, ArrowRight } from "lucide-react";
import acgLogo from "/acg-logo.webp";
import memVip from "@/assets/mem-feature-vip.png";
import memTransfer from "@/assets/mem-feature-transfer.png";
import memCard from "@/assets/mem-feature-card.png";
import valuePerformance from "@/assets/value-performance.jpg";
import valueEfficiency from "@/assets/value-efficiency.jpg";
import valueBills from "@/assets/value-bills.jpg";
import valueLifespan from "@/assets/value-lifespan.jpg";
import valueBreakdowns from "@/assets/value-breakdowns.jpg";
import valueSafety from "@/assets/value-safety.jpg";

const features = [
  {
    icon: memVip,
    title: "VIP Priority",
    description: "Completely avoid booking & servicing inconveniences",
  },
  {
    icon: memTransfer,
    title: "Fully Transferable",
    description: "Transfer your agreement over to your new home in the future",
  },
];

type FilterCategory = "all" | "vip" | "maintenance" | "warranties" | "extras";

const filters: { label: string; value: FilterCategory }[] = [
  { label: "Show All", value: "all" },
  { label: "VIP Discounts", value: "vip" },
  { label: "Maintenance & Services", value: "maintenance" },
  { label: "Warranties", value: "warranties" },
  { label: "Extras Benefits", value: "extras" },
];

const benefitCards: { title: string; description: string; bg: string; category: FilterCategory[] }[] = [
  { title: "$230", description: "Service Call For Break Down Service Calls", bg: "bg-red-600", category: ["vip"] },
  { title: "$198", description: "General Maintenance", bg: "bg-blue-600", category: ["maintenance"] },
  { title: "SAVE 15%", description: "With Senior Discounts On All Services", bg: "bg-red-500", category: ["vip"] },
  { title: "Upgrade Your A/C With Wifi", description: "Control It From Any Where from $499", bg: "bg-green-600", category: ["extras"] },
  { title: "Air Filter & Sanitiser", description: "Replace Your air conditioner filter from $250", bg: "bg-green-500", category: ["maintenance"] },
  { title: "10 Year Warranty*", description: "", bg: "bg-acg-charcoal", category: ["warranties"] },
  { title: "$220", description: "Fixed Tune-Up Pricing for the Lifetime Of Your plan", bg: "bg-acg-charcoal", category: ["vip"] },
  { title: "Emergency Service Available", description: "", bg: "bg-acg-charcoal", category: ["maintenance"] },
  { title: "Priority Service Available", description: "Same Day Booking Call for price.", bg: "bg-acg-charcoal", category: ["extras"] },
  { title: "Preventative Maintenance Plans Available", description: "", bg: "bg-acg-charcoal", category: ["maintenance"] },
  { title: "All Spare Parts Available For Purchase", description: "", bg: "bg-acg-charcoal", category: ["extras"] },
  { title: "24 Hour Technician", description: "On Site Capability", bg: "bg-acg-charcoal", category: ["extras"] },
];

const valueCards = [
  { image: valuePerformance, title: "Improve System Performance by Restoring Lost Capacity" },
  { image: valueEfficiency, title: "Increase Energy Efficiency" },
  { image: valueBills, title: "Reduces up to 30% High Electricity Bills" },
  { image: valueLifespan, title: "Extend Your Equipment's Lifespan" },
  { image: valueBreakdowns, title: "Prevent Costly Breakdowns & Repairs" },
  { image: valueSafety, title: "Improve System Safety" },
];

const Membership = () => {
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("all");
  const filteredCards = activeFilter === "all" ? benefitCards : benefitCards.filter(c => c.category.includes(activeFilter));

  return (
    <div className="min-h-screen bg-background">
      <PageSEO title="ACG Membership | VIP AC Service & Discounts | Southern Highlands" description="Join ACG Membership for VIP priority servicing, up to 20% off, extended warranties and annual maintenance reminders. Protect your air conditioning investment." />
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Membership" }]} />

      {/* Hero Section */}
      <section className="relative bg-acg-dark py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-acg-charcoal via-acg-dark to-acg-charcoal" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="w-8 h-8 border-t-4 border-l-4 border-secondary mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-primary-foreground leading-tight mb-4">
                ACG Membership
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-secondary mb-6">
                Maintaining the Best Aircon Experience
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Up to 20% off exclusive discounts on select services permanently",
                  "VIP priority servicing - we'll call you every year, at the right time",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-primary-foreground/80">
                    <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/about"
                className="inline-flex items-center gap-2 bg-secondary text-primary-foreground font-bold px-8 py-4 rounded hover:opacity-90 transition-opacity uppercase text-sm tracking-wider"
              >
                Become a Member
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:w-1/2 flex justify-center lg:justify-end">
              <img
                src={acgLogo}
                alt="ACG Southern Highlands — ACG Membership program logo"
                className="w-full max-w-lg drop-shadow-2xl"
                width={512}
                height={512}
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-16">
            {features.map((feature) => (
              <div key={feature.title} className="flex items-center gap-4">
                <img src={feature.icon} alt={`${feature.title} — ACG membership benefit icon`} className="w-16 h-16 object-contain" width={64} height={64} loading="lazy" />
                <div>
                  <h3 className="font-bold text-foreground text-lg">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protect Your Home */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img
                src={memCard}
                alt="ACG Membership card — VIP priority servicing and exclusive discounts for air conditioning maintenance"
                className="w-full max-w-lg mx-auto rounded-lg shadow-xl"
                width={512}
                height={320}
                loading="lazy"
              />
            </div>
            <div className="lg:w-1/2">
              <div className="w-8 h-8 border-t-4 border-l-4 border-primary mb-6" />
              <h2 className="text-3xl md:text-4xl font-black text-foreground mb-4">
                Protect Your Home
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Having things that should be working on a daily basis to suddenly stop for no apparent reason, has to be one of the most frustrating and stressful moments especially during crucial times.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our customers have been able to completely avoid these scenarios through our thorough and regular maintenance. We call and check up on you every year without fail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Preventive Maintenance */}
      <section className="py-16 bg-acg-dark">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4">
            Preventive Maintenance
          </h2>
          <p className="text-secondary font-semibold text-lg mb-4">
            Keep your asset in top shape all year round
          </p>
          <p className="text-primary-foreground/70 max-w-2xl mx-auto leading-relaxed">
            It is very easy to forget about your air conditioning, heating and electrical systems when they're working, but it's when they stop which causes huge inconveniences. The most effective way to prevent this from happening is through regular inspections and maintenance.
          </p>
          <p className="text-primary-foreground/70 max-w-2xl mx-auto leading-relaxed mt-4">
            With our ACG Membership Plan we will maintain your systems in the best shape possible, without you having to ever think about it. Have peace of mind and comfort!
          </p>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-20 bg-acg-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black text-primary-foreground mb-3">
              Membership Benefits
            </h2>
            <p className="text-primary-foreground/70 max-w-lg mx-auto">
              Over 12 exclusive benefits to take advantage of!
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              {filters.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setActiveFilter(f.value)}
                  className={`px-5 py-2 rounded-full border text-sm font-semibold transition-all ${
                    activeFilter === f.value
                      ? "bg-primary-foreground text-acg-dark border-primary-foreground"
                      : "border-primary-foreground/40 text-primary-foreground/70 hover:border-primary-foreground hover:text-primary-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCards.map((card) => (
              <div
                key={card.title}
                className={`${card.bg} rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-[160px] transition-transform hover:scale-[1.02]`}
              >
                <h3 className="text-2xl md:text-3xl font-black text-primary-foreground leading-tight">{card.title}</h3>
                {card.description && (
                  <p className="text-sm md:text-base text-primary-foreground/90 font-semibold mt-2">{card.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value & Efficiency */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="mb-14">
            <div className="w-8 h-8 border-t-4 border-l-4 border-secondary mb-6" />
            <h2 className="text-3xl md:text-5xl font-black text-foreground mb-4">
              Value & Efficiency
            </h2>
            <p className="text-secondary font-semibold text-lg">
              We inspect, clean and tune-up your systems to ensure they're operating at 100%
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {valueCards.map((card) => (
              <div
                key={card.title}
                className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg hover:border-secondary/30 transition-all"
              >
                <img src={card.image} alt={`${card.title} — ACG air conditioning value and efficiency benefit`} className="w-full h-48 object-cover" loading="lazy" width={640} height={192} />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-foreground leading-snug">{card.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flexible Payments CTA */}
      <section className="relative py-20 bg-acg-charcoal overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-xl">
            <div className="w-8 h-8 border-t-4 border-l-4 border-primary mb-6" />
            <h2 className="text-3xl md:text-4xl font-black mb-2 text-destructive-foreground">
              Flexible Payments Starting from only
            </h2>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-6xl md:text-7xl font-black text-primary-foreground">$7.00</span>
              <span className="text-xl text-primary-foreground/60">/ per week</span>
            </div>
            <div className="inline-block bg-secondary text-primary-foreground font-black text-lg px-4 py-2 rounded mb-8">
              SAVE $120!
            </div>
            <div>
              <a
                href="/about"
                className="inline-flex items-center gap-2 bg-secondary text-primary-foreground font-bold px-8 py-4 rounded hover:opacity-90 transition-opacity uppercase text-sm tracking-wider"
              >
                Become a Member Today
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* Whats Included */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">What ACG Membership Includes</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            ACG Membership is built around one simple idea: your heating and cooling system should be looked after before it fails, not after. Rather than waiting for a breakdown on the coldest morning of the year, members are proactively contacted each year to book their scheduled service at the right time — ahead of winter, when demand on booking diaries is highest across the Southern Highlands.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Membership brings together annual servicing reminders, VIP priority booking, discounted rates on selected services, and fixed tune-up pricing for the life of the plan. It's designed for homeowners, holiday rental managers, heritage property owners and commercial site managers who would rather budget for planned maintenance than risk an unplanned outage in a Mittagong, Bowral or Moss Vale frost.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Priority Servicing When It Matters Most</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            In a region that sees genuine sub-zero mornings and elevation between roughly 600 and 750 metres, the busiest time on our books is always the run-up to winter. Homes relying on reverse-cycle systems for heating simply can't afford to be at the back of a queue when temperatures drop and everyone else calls at once.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            As a member, you're moved to the front of that queue. We contact you ahead of the cold season to schedule your service at a convenient time, rather than you having to remember to call — and if something does go wrong, members receive priority attention over new or one-off bookings.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Why Pre-Winter Servicing Matters in a Frost-Prone Climate</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Reverse-cycle air conditioners work hardest exactly when conditions are toughest — during sub-zero starts and prolonged cold snaps typical of Bowral, Berrima, Robertson and Bundanoon winters. A system that hasn't been checked before the season starts is far more likely to underperform, ice up, or fail entirely on the mornings you need it most.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            An annual pre-winter service checks that outdoor units are clear of debris and positioned to cope with frost, that refrigerant levels and defrost cycles are correct, and that filters and coils are clean enough for the unit to draw heat efficiently from cold outside air. Skipping this step doesn't just risk comfort — it also means the system is working harder than it needs to, which can shorten its lifespan and increase running costs over a cold Southern Highlands winter.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">What a Service Visit Covers</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            A typical membership service visit is a thorough, hands-on inspection rather than a quick glance. Depending on your system, it generally includes:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li>Cleaning or replacing filters and checking indoor coil condition</li>
            <li>Inspecting and clearing the outdoor unit of leaves, debris and obstructions common after autumn</li>
            <li>Checking refrigerant levels and system pressures for correct operation</li>
            <li>Testing defrost cycle performance, which is critical for reliable heating in frosty conditions</li>
            <li>Inspecting electrical connections, capacitors and condensate drainage</li>
            <li>Checking thermostat and control settings, and confirming safe, efficient operation</li>
            <li>Reporting any parts approaching the end of their working life, so replacements can be planned rather than becoming emergencies</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You'll receive a summary of what was checked and any recommendations, so there's a clear record of your system's condition heading into the coldest months.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Membership and Warranty Compliance</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Most manufacturers require evidence of regular professional servicing to keep a warranty valid, particularly for systems working hard through cold Southern Highlands winters. Missing scheduled maintenance can put a warranty claim at risk if something later fails.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Because ACG Membership includes an annual reminder and scheduled visit, it helps keep your servicing history documented and up to date — giving you a straightforward record to point to if a manufacturer warranty claim is ever needed, and reducing the chance of a claim being knocked back due to a gap in maintenance.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Who Membership Suits</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Membership is a good fit for a wide range of property types across the region, including:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
            <li><span className="font-semibold text-foreground">Heritage homes</span> in Berrima, Bowral and Moss Vale, where retrofitted systems often need careful, experienced attention to work well within older buildings</li>
            <li><span className="font-semibold text-foreground">Holiday rentals</span> in Bundanoon, Robertson and surrounding villages, where owners can't be on-site to notice a fault and guests expect reliable heating on arrival</li>
            <li><span className="font-semibold text-foreground">Commercial sites</span> such as cafes, offices and retail premises, where a heating or cooling failure directly affects staff, customers and trading hours</li>
            <li><span className="font-semibold text-foreground">Large country properties</span> around Robertson, Bundanoon and Goulburn, which are more exposed to frost and often run larger, multi-zone systems that benefit from a scheduled maintenance plan</li>
          </ul>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">How to Join</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Joining ACG Membership is straightforward. Get in touch with our team on <a href="tel:0480841485" className="text-primary font-semibold">0480 841 485</a> or via our <a href="/contact-us" className="text-primary font-semibold">contact page</a>, and we'll talk through your property, existing system and the plan that best suits your needs. Once you're signed up, we'll be in touch each year to book your service at the right time — you won't need to keep track of it yourself.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Membership FAQ</h2>
          <div className="space-y-6">
            {[
              { q: "How often is my system serviced as a member?", a: "Members receive a scheduled annual service, timed ahead of winter so your heating is checked and ready before the coldest weather arrives." },
              { q: "Do I need to remember to book my own service?", a: "No — one of the key benefits of membership is that we contact you each year at the right time to arrange your visit, rather than leaving it up to you to remember." },
              { q: "Does membership cover emergency breakdowns?", a: "Membership gives you priority access to book a service, including faster attention if something goes wrong between scheduled visits, ahead of new or one-off enquiries." },
              { q: "Will membership affect my manufacturer warranty?", a: "Regular servicing under membership helps you meet most manufacturers' requirement for documented maintenance, which can be important if a warranty claim is ever needed." },
              { q: "Is membership suitable for holiday rental properties?", a: "Yes. It's a popular option for owners who can't personally check on a property, as it keeps the system maintained and reduces the risk of guests arriving to a fault." },
              { q: "Can membership be transferred if I sell my home?", a: "Yes, ACG Membership is fully transferable, so a new owner can continue the plan when the property changes hands." },
              { q: "What areas does membership cover?", a: "We provide membership servicing across Mittagong, Bowral, Moss Vale, Berrima, Burradoo, Bundanoon, Robertson, Goulburn, Picton, Tahmoor, Bargo, Thirlmere and surrounding Southern Highlands areas." },
              { q: "How do I sign up?", a: "Call us on 0480 841 485 or reach out through our contact page, and we'll set up your membership and schedule your first service." },
            ].map((faq) => (
              <div key={faq.q} className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-bold text-foreground mb-2">{faq.q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">The Cost of Skipping Annual Servicing</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            It can be tempting to skip a service when a system still seems to be working, but in a cold-climate area like the Southern Highlands, small issues tend to surface at the worst possible moment — on the first genuinely frosty morning of the season. A slightly low refrigerant charge, a dirty outdoor coil or a faulty defrost sensor might not be obvious in mild weather, but can mean the difference between a system that heats reliably and one that struggles, ices up or trips out once temperatures drop close to zero.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Beyond comfort, deferred maintenance often costs more in the long run. Components under strain from a build-up of dust, restricted airflow or low refrigerant tend to fail sooner, turning a routine service item into an emergency repair — usually during the busiest and least convenient time of year to get a technician out quickly.
          </p>

          <h2 className="text-3xl md:text-4xl font-black text-foreground mt-12 mb-6">Membership for Larger and More Complex Properties</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Larger country properties and commercial sites across Robertson, Goulburn and the wider Southern Highlands often run multiple zones, ducted systems or several standalone units across a single site. Keeping track of service history for each system individually can be difficult without a structured plan.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Membership gives these properties a consistent, documented maintenance schedule across every system on-site, rather than relying on ad hoc bookings whenever something starts to underperform. For heritage buildings in particular, where retrofitted ducting and older wall or window openings can complicate airflow, having the same technicians return each year also means issues specific to that property are more likely to be picked up early, since they already understand its quirks.
          </p>
        </div>
      </section>

      <CTAFooter />
    </div>
  );
};

export default Membership;
